import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@/services/api/client";

export type VersionedAutosaveStatus =
  | "idle"
  | "dirty"
  | "saving"
  | "saved"
  | "error"
  | "conflict";

interface VersionedResult {
  version: number;
}

interface VersionedAutosaveOptions<TPayload, TResult extends VersionedResult> {
  initialVersion: number;
  save: (payload: TPayload, expectedVersion: number) => Promise<TResult>;
  debounceMs?: number;
  enabled?: boolean;
  onSaved?: (result: TResult) => void;
}

interface FlushOptions<TPayload> {
  payload?: TPayload;
  force?: boolean;
}

export interface VersionedAutosaveController<TPayload> {
  status: VersionedAutosaveStatus;
  version: number;
  error: unknown;
  hasUnsavedChanges: boolean;
  queue: (payload: TPayload) => void;
  retry: () => Promise<boolean>;
  flush: (options?: FlushOptions<TPayload>) => Promise<boolean>;
  reset: (version: number) => void;
}

export function useVersionedAutosave<
  TPayload,
  TResult extends VersionedResult = VersionedResult,
>({
  initialVersion,
  save,
  debounceMs = 800,
  enabled = true,
  onSaved
}: VersionedAutosaveOptions<TPayload, TResult>): VersionedAutosaveController<TPayload> {
  const [status, setStatus] = useState<VersionedAutosaveStatus>("idle");
  const [version, setVersion] = useState(initialVersion);
  const [error, setError] = useState<unknown>(undefined);
  const saveRef = useRef(save);
  const onSavedRef = useRef(onSaved);
  const enabledRef = useRef(enabled);
  const versionRef = useRef(initialVersion);
  const latestPayloadRef = useRef<TPayload>();
  const revisionRef = useRef(0);
  const savedRevisionRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const inFlightRef = useRef<Promise<boolean>>();
  const flushAfterFlightRef = useRef(false);
  const mountedRef = useRef(true);

  saveRef.current = save;
  onSavedRef.current = onSaved;
  enabledRef.current = enabled;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== undefined) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  const performSave = useCallback(async (): Promise<boolean> => {
    clearTimer();
    if (!enabledRef.current || latestPayloadRef.current === undefined) {
      return false;
    }
    if (inFlightRef.current) {
      flushAfterFlightRef.current = true;
      return inFlightRef.current;
    }

    const payload = latestPayloadRef.current;
    const savingRevision = revisionRef.current;
    if (mountedRef.current) {
      setStatus("saving");
      setError(undefined);
    }

    const request = saveRef
      .current(payload, versionRef.current)
      .then((result) => {
        versionRef.current = result.version;
        savedRevisionRef.current = savingRevision;
        if (mountedRef.current) {
          setVersion(result.version);
          onSavedRef.current?.(result);
          setStatus(revisionRef.current === savingRevision ? "saved" : "dirty");
        }
        return true;
      })
      .catch((caught: unknown) => {
        if (mountedRef.current) {
          setError(caught);
          setStatus(isConflict(caught) ? "conflict" : "error");
        }
        return false;
      })
      .finally(() => {
        inFlightRef.current = undefined;
        if (
          flushAfterFlightRef.current &&
          revisionRef.current > savedRevisionRef.current &&
          enabledRef.current
        ) {
          flushAfterFlightRef.current = false;
          void performSave();
        } else {
          flushAfterFlightRef.current = false;
        }
      });
    inFlightRef.current = request;
    return request;
  }, [clearTimer]);

  const schedule = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = undefined;
      void performSave();
    }, debounceMs);
  }, [clearTimer, debounceMs, performSave]);

  const queue = useCallback(
    (payload: TPayload) => {
      latestPayloadRef.current = payload;
      revisionRef.current += 1;
      setError(undefined);
      setStatus("dirty");
      if (enabledRef.current) {
        schedule();
      }
    },
    [schedule],
  );

  const flush = useCallback(
    async (options?: FlushOptions<TPayload>) => {
      if (options && "payload" in options && options.payload !== undefined) {
        latestPayloadRef.current = options.payload;
        if (options.force) {
          revisionRef.current += 1;
        }
      } else if (options?.force && latestPayloadRef.current !== undefined) {
        revisionRef.current += 1;
      }
      clearTimer();
      if (inFlightRef.current) {
        flushAfterFlightRef.current = revisionRef.current > savedRevisionRef.current;
        const firstResult = await inFlightRef.current;
        if (revisionRef.current > savedRevisionRef.current) {
          return performSave();
        }
        return firstResult;
      }
      if (revisionRef.current <= savedRevisionRef.current) {
        return true;
      }
      return performSave();
    },
    [clearTimer, performSave],
  );

  const retry = useCallback(() => flush(), [flush]);

  const reset = useCallback(
    (nextVersion: number) => {
      clearTimer();
      latestPayloadRef.current = undefined;
      revisionRef.current = 0;
      savedRevisionRef.current = 0;
      flushAfterFlightRef.current = false;
      versionRef.current = nextVersion;
      setVersion(nextVersion);
      setError(undefined);
      setStatus("idle");
    },
    [clearTimer],
  );

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (revisionRef.current <= savedRevisionRef.current) {
        return;
      }
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimer();
    };
  }, [clearTimer]);

  return {
    status,
    version,
    error,
    hasUnsavedChanges: revisionRef.current > savedRevisionRef.current,
    queue,
    retry,
    flush,
    reset
  };
}

function isConflict(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}
